import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { RECURRING_FREQUENCIES, UNITS, parseNumber, today } from '../labels';
import { ItemChoice, ItemPicker, Loader, useConfirm, useLoad, useNotify } from '../common';

const KINDS: { value: T.CatalogItemKind; text: string }[] = [
  { value: 'Household', text: 'Casa (limpeza, higiene…)' },
  { value: 'Food', text: 'Alimentação' },
];

/** Create (/regulares/nova) or edit (/regulares/:id) a regular purchase. */
const RecurringItemEditPage: React.FC = () => {
  const { id } = useParams();
  const isNew = !id || id === 'nova';
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const { data, error: loadError, loading, reload } = useLoad(async () => {
    const [stores, items] = await Promise.all([api.getStores(), isNew ? Promise.resolve([] as T.RecurringItem[]) : api.getRecurringItems()]);
    return { stores, item: isNew ? null : items.find(i => i.publicId === id) ?? null };
  }, [id]);

  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [kind, setKind] = useState<T.CatalogItemKind>('Household');
  const [frequency, setFrequency] = useState<T.RecurringFrequency>('Biweekly');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<T.MeasureUnit | ''>('');
  const [storeId, setStoreId] = useState('');
  const [next, setNext] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const item = data?.item ?? null;
  useEffect(() => {
    if (!item) return;
    setFrequency(item.frequency);
    setQuantity(item.quantity != null ? String(item.quantity).replace('.', ',') : '');
    setUnit(item.unit ?? '');
    setStoreId(item.storePublicId ?? '');
    setNext(item.nextDueOn);
  }, [item]);

  const back = () => navigate('/casa/compras/regulares');

  const choose = (value: ItemChoice | null) => {
    setChoice(value);
    if (value && 'item' in value) setUnit(value.item.defaultUnit);
  };

  const save = async () => {
    setError(null);
    if (isNew && !choice) { setError('Indica o artigo.'); return; }
    let value: number | null = null;
    if (quantity.trim()) {
      value = parseNumber(quantity);
      if (value === null || value <= 0) { setError('A quantidade deve ser um número positivo.'); return; }
    }
    const newName = choice && 'newName' in choice ? choice.newName : null;
    const request: T.SaveRecurringItemRequest = {
      catalogItemPublicId: item ? item.catalogItemPublicId : choice && 'item' in choice ? choice.item.publicId : null,
      newItemName: newName,
      newItemKind: newName ? kind : null,
      newItemSection: newName && kind === 'Household' ? 'Household' : null,
      quantity: value,
      unit: unit || null,
      storePublicId: storeId || null,
      frequency,
      nextDueOn: next || null,
    };
    setBusy(true);
    try {
      if (item) await api.updateRecurringItem(item.publicId, request);
      else await api.createRecurringItem(request);
      notify('Compra regular guardada.', { severity: 'success' });
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    if (!await confirm({ title: 'Compra regular', text: `Deixar de propor "${item.name}"?`, confirmText: 'Sim', danger: true })) return;
    setBusy(true);
    try {
      await api.deleteRecurringItem(item.publicId);
      notify(`${item.name} deixou de ser uma compra regular.`, { severity: 'success' });
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={back}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>{item?.name ?? 'Compra regular'}</Typography>
      </Stack>
      <Loader loading={loading} error={loadError} onRetry={reload} hasData={data !== undefined}>
        {data && !isNew && !item && <Alert severity="warning">Esta compra regular já não existe.</Alert>}
        {data && (isNew || item) && (
          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            {isNew ? (
              <>
                <ItemPicker value={choice} onChange={choose} label="Artigo (ex.: Papel higiénico)" autoFocus />
                {choice && 'newName' in choice && (
                  <TextField select label="Tipo" value={kind} onChange={e => setKind(e.target.value as T.CatalogItemKind)}
                    helperText="Artigo novo: fica no catálogo da casa.">
                    {KINDS.map(k => <MenuItem key={k.value} value={k.value}>{k.text}</MenuItem>)}
                  </TextField>
                )}
              </>
            ) : (
              <TextField label="Artigo" value={item?.name ?? ''} disabled />
            )}
            <TextField select label="De quanto em quanto tempo" value={frequency} onChange={e => setFrequency(e.target.value as T.RecurringFrequency)}>
              {RECURRING_FREQUENCIES.map(f => <MenuItem key={f.value} value={f.value}>{f.text}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Quantidade (opcional)" value={quantity} onChange={e => setQuantity(e.target.value)}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }} fullWidth
              />
              <TextField select label="Unidade" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit | '')} fullWidth>
                <MenuItem value="">—</MenuItem>
                {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField select label="Loja" value={storeId} onChange={e => setStoreId(e.target.value)}>
              <MenuItem value="">Qualquer loja</MenuItem>
              {data.stores.map(s => <MenuItem key={s.publicId} value={s.publicId}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              type="date" label="Próxima vez" value={next} onChange={e => setNext(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
              {item && <Button color="error" onClick={remove} disabled={busy}>Deixar de comprar regularmente</Button>}
            </Box>
          </Stack>
        )}
      </Loader>
      {confirmDialog}
    </>
  );
};

export default RecurringItemEditPage;
